import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, X, Check, ChevronLeft, ChevronRight, FileCheck } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import { PANEL_FILTERS } from "@/data/content";

const STEPS = ["Contact", "Matter", "Location & Dates", "Documents", "Review"];
const AREA_OPTIONS = PANEL_FILTERS.slice(1);

const emptyForm = {
  full_name: "", mobile: "", email: "", preferred_language: "English", preferred_contact: "Phone",
  practice_area: "", description: "", court_forum: "", case_stage: "", opposite_party: "", urgency: "Standard",
  city: "", court_location: "", next_hearing: "", deadline: "", documents: [], consent: false, website: "",
};

const readFile = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result.split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function IntakeForm() {
  const location = useLocation();
  const initialArea = location.state?.area || "";
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...emptyForm, practice_area: initialArea });
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const stepValid = useMemo(() => {
    if (step === 0) return form.full_name.trim().length >= 2 && form.mobile.trim().length >= 8 && /\S+@\S+\.\S+/.test(form.email);
    if (step === 1) return !!form.practice_area && form.description.trim().length >= 10;
    if (step === 4) return form.consent;
    return true;
  }, [step, form]);

  const addFiles = async (files) => {
    const list = Array.from(files).slice(0, 4 - form.documents.length);
    for (const f of list) {
      if (f.size > 2 * 1024 * 1024) {
        toast.error(`${f.name} exceeds the 2 MB per-file limit`);
        continue;
      }
      if (!/(pdf|image)/.test(f.type)) {
        toast.error(`${f.name}: only PDF and image files are accepted`);
        continue;
      }
      const data = await readFile(f);
      set("documents", [...form.documents, { name: f.name, type: f.type, data }]);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await API.post("/enquiries", form);
      setReference(data.reference);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (reference) {
    return (
      <motion.div
        data-testid="intake-confirmation"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border border-gold/30 bg-navy p-10 text-center sm:p-14"
      >
        <div className="mx-auto flex h-16 w-16 items-center justify-center border border-gold/60">
          <FileCheck className="text-gold" size={26} />
        </div>
        <p className="mono-label mt-8">Enquiry Received</p>
        <p className="mt-5 font-serif text-4xl font-medium tracking-tight text-ivory" data-testid="intake-reference-number">{reference}</p>
        <p className="mx-auto mt-6 max-w-lg text-sm leading-relaxed text-ivory/60">
          Thank you. Your information has been received for preliminary review. Submission of this form does not create
          an advocate-client relationship or confirm acceptance of the matter. The team will communicate the appropriate
          next step after review. Please quote your reference number in future correspondence.
        </p>
        <a href="/track" data-testid="intake-track-link" className="btn-outline-gold mt-10 inline-flex">
          Track This Enquiry
        </a>
      </motion.div>
    );
  }

  return (
    <div data-testid="confidential-intake-form" className="border border-ivory/10 bg-oxford/40">
      <div className="border-b border-ivory/10 px-6 py-6 sm:px-10">
        <p className="mono-label">Confidential Matter Intake</p>
        <div className="mt-5 flex items-center gap-0 overflow-x-auto">
          {STEPS.map((s, i) => (
            <div key={s} className="flex shrink-0 items-center">
              <button
                data-testid={`intake-step-tab-${i}`}
                onClick={() => i < step && setStep(i)}
                className={`flex items-center gap-2.5 ${i <= step ? "cursor-pointer" : "cursor-default"}`}
                aria-current={i === step ? "step" : undefined}
              >
                <span className={`flex h-7 w-7 items-center justify-center border font-mono text-[10px] transition-colors duration-300 ${i < step ? "border-gold bg-gold text-navy" : i === step ? "border-gold text-gold" : "border-ivory/20 text-ivory/30"}`}>
                  {i < step ? <Check size={12} /> : `0${i + 1}`}
                </span>
                <span className={`hidden font-mono text-[10px] uppercase tracking-[0.18em] sm:block ${i === step ? "text-ivory" : "text-ivory/35"}`}>{s}</span>
              </button>
              {i < STEPS.length - 1 && <span className={`mx-3 h-px w-6 sm:w-10 ${i < step ? "bg-gold" : "bg-ivory/15"}`} />}
            </div>
          ))}
        </div>
      </div>

      <div className="px-6 py-8 sm:px-10 sm:py-10">
        <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" />
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {step === 0 && (
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="intake-name">Full Name *</label>
                  <input id="intake-name" data-testid="intake-name-input" className="field-input-dark" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="As per identity records" />
                </div>
                <div>
                  <label className="field-label" htmlFor="intake-mobile">Mobile Number *</label>
                  <input id="intake-mobile" data-testid="intake-mobile-input" className="field-input-dark" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+91" />
                </div>
                <div>
                  <label className="field-label" htmlFor="intake-email">Email Address *</label>
                  <input id="intake-email" data-testid="intake-email-input" type="email" className="field-input-dark" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="name@example.com" />
                </div>
                <div>
                  <label className="field-label" htmlFor="intake-language">Preferred Language</label>
                  <select id="intake-language" data-testid="intake-language-select" className="field-input-dark" value={form.preferred_language} onChange={(e) => set("preferred_language", e.target.value)}>
                    <option>English</option><option>Telugu</option><option>Hindi</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <span className="field-label">Preferred Contact Method</span>
                  <div className="flex gap-3">
                    {["Phone", "Email"].map((m) => (
                      <button key={m} data-testid={`intake-contact-method-${m.toLowerCase()}`} onClick={() => set("preferred_contact", m)} className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${form.preferred_contact === m ? "border-gold bg-gold/10 text-gold" : "border-ivory/20 text-ivory/50"}`}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="grid gap-6">
                <div>
                  <label className="field-label" htmlFor="intake-area">Practice Area *</label>
                  <select id="intake-area" data-testid="intake-area-select" className="field-input-dark" value={form.practice_area} onChange={(e) => set("practice_area", e.target.value)}>
                    <option value="">Select the general nature of the matter</option>
                    {AREA_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                    <option value="General Assessment">Not Sure — General Assessment</option>
                  </select>
                </div>
                <div>
                  <label className="field-label" htmlFor="intake-description">Brief Factual Summary *</label>
                  <textarea id="intake-description" data-testid="intake-description-input" rows={5} className="field-input-dark" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="A short, factual summary with key dates. Do not include privileged or highly sensitive detail." />
                </div>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label className="field-label" htmlFor="intake-court">Court or Forum, if applicable</label>
                    <input id="intake-court" data-testid="intake-court-input" className="field-input-dark" value={form.court_forum} onChange={(e) => set("court_forum", e.target.value)} placeholder="e.g. District Court, NTR District" />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="intake-stage">Present Case Stage</label>
                    <input id="intake-stage" data-testid="intake-stage-input" className="field-input-dark" value={form.case_stage} onChange={(e) => set("case_stage", e.target.value)} placeholder="e.g. FIR registered, notice received" />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="intake-opposite">Opposite Party Name (Conflict Check)</label>
                    <input id="intake-opposite" data-testid="intake-opposite-input" className="field-input-dark" value={form.opposite_party} onChange={(e) => set("opposite_party", e.target.value)} />
                  </div>
                  <div>
                    <span className="field-label">Urgency Level</span>
                    <div className="flex gap-3">
                      {["Standard", "Time-Sensitive", "Urgent"].map((u) => (
                        <button key={u} data-testid={`intake-urgency-${u.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => set("urgency", u)} className={`border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${form.urgency === u ? "border-gold bg-gold/10 text-gold" : "border-ivory/20 text-ivory/50"}`}>
                          {u}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="intake-city">City</label>
                  <input id="intake-city" data-testid="intake-city-input" className="field-input-dark" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Vijayawada" />
                </div>
                <div>
                  <label className="field-label" htmlFor="intake-court-location">Relevant Court Location</label>
                  <input id="intake-court-location" data-testid="intake-court-location-input" className="field-input-dark" value={form.court_location} onChange={(e) => set("court_location", e.target.value)} />
                </div>
                <div>
                  <label className="field-label" htmlFor="intake-hearing">Next Hearing Date, if applicable</label>
                  <input id="intake-hearing" data-testid="intake-hearing-input" type="date" className="field-input-dark" value={form.next_hearing} onChange={(e) => set("next_hearing", e.target.value)} />
                </div>
                <div>
                  <label className="field-label" htmlFor="intake-deadline">Important Limitation or Deadline, if known</label>
                  <input id="intake-deadline" data-testid="intake-deadline-input" className="field-input-dark" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} placeholder="e.g. notice period ends 15 August" />
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <p className="text-sm leading-relaxed text-ivory/55">
                  You may attach up to four documents (PDF or image, 2 MB each) that help identify the matter &mdash; for
                  example a notice, FIR copy or order. Do not upload privileged or highly sensitive material at this
                  stage.
                </p>
                <label
                  data-testid="intake-upload-zone"
                  className="mt-6 flex cursor-pointer flex-col items-center justify-center gap-3 border border-dashed border-gold/40 px-6 py-12 text-center transition-colors hover:border-gold hover:bg-gold/5"
                >
                  <Upload size={20} className="text-gold" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/70">Select Documents</span>
                  <span className="font-mono text-[10px] tracking-[0.1em] text-slate">PDF, JPG, PNG &middot; Max 2 MB each</span>
                  <input data-testid="intake-file-input" type="file" multiple accept=".pdf,image/*" className="hidden" onChange={(e) => addFiles(e.target.files)} />
                </label>
                {form.documents.length > 0 && (
                  <ul className="mt-6 space-y-2" data-testid="intake-documents-list">
                    {form.documents.map((d, i) => (
                      <li key={i} className="flex items-center justify-between border border-ivory/10 px-4 py-3">
                        <span className="truncate font-mono text-xs text-ivory/70">{d.name}</span>
                        <button data-testid={`intake-remove-doc-${i}`} onClick={() => set("documents", form.documents.filter((_, x) => x !== i))} aria-label={`Remove ${d.name}`} className="text-ivory/40 hover:text-gold">
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-6 font-mono text-[10px] leading-relaxed tracking-[0.06em] text-slate">
                  Confidentiality notice: documents submitted through this form are transmitted for preliminary review
                  only and do not by themselves create an advocate-client relationship.
                </p>
              </div>
            )}

            {step === 4 && (
              <div>
                <div className="border border-ivory/10">
                  {[
                    ["Name", form.full_name], ["Mobile", form.mobile], ["Email", form.email],
                    ["Practice Area", form.practice_area], ["Court / Forum", form.court_forum || "—"],
                    ["Case Stage", form.case_stage || "—"], ["Opposite Party", form.opposite_party || "—"],
                    ["Urgency", form.urgency], ["City", form.city || "—"],
                    ["Next Hearing", form.next_hearing || "—"], ["Deadline", form.deadline || "—"],
                    ["Documents", form.documents.length ? `${form.documents.length} attached` : "None"],
                  ].map(([k, v]) => (
                    <div key={k} className="grid grid-cols-3 gap-4 border-b border-ivory/5 px-5 py-3 last:border-0">
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">{k}</span>
                      <span className="col-span-2 text-sm text-ivory/80">{v}</span>
                    </div>
                  ))}
                  <div className="border-b border-ivory/5 px-5 py-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">Summary</span>
                    <p className="mt-1 text-sm leading-relaxed text-ivory/80">{form.description}</p>
                  </div>
                </div>
                <p className="mt-6 border-l-2 border-gold/50 pl-4 text-[13px] leading-relaxed text-ivory/55">
                  Do not submit confidential, privileged or highly sensitive information through this form. Submission of
                  information does not by itself create an advocate-client relationship.
                </p>
                <label className="mt-6 flex cursor-pointer items-start gap-3">
                  <input data-testid="intake-consent-checkbox" type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-[#AE8A4A]" />
                  <span className="text-sm leading-relaxed text-ivory/70">
                    I confirm the information provided is accurate to the best of my knowledge, and I consent to Smart
                    Legal Solutions contacting me regarding this enquiry.
                  </span>
                </label>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex items-center justify-between border-t border-ivory/10 pt-8">
          <button
            data-testid="intake-prev-step-btn"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="btn-ghost-ivory disabled:opacity-30"
          >
            <ChevronLeft size={13} /> Back
          </button>
          {step < 4 ? (
            <button
              data-testid="intake-next-step-btn"
              onClick={() => stepValid && setStep((s) => s + 1)}
              disabled={!stepValid}
              className="btn-outline-gold disabled:cursor-not-allowed disabled:opacity-30"
            >
              Continue <ChevronRight size={13} />
            </button>
          ) : (
            <button
              data-testid="intake-submit-btn"
              onClick={submit}
              disabled={!form.consent || submitting}
              className="btn-outline-gold disabled:cursor-not-allowed disabled:opacity-30"
            >
              {submitting ? "Submitting…" : "Submit Matter Details"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
